import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { WidgetBoundary } from '@/components/widgets/WidgetFrame';
import type { HomeLayout } from '@/pages/home/useHomeLayout';
import type { WidgetSize } from '@/pages/home/widgets/registry';

/**
 * 위젯을 <b>메이슨리</b>(자유형) 또는 <b>격자</b>(격자형)로 쌓는다. front 의 `WidgetMasonry` 를 옮겼다.
 *
 * <h2>자유형 — 메이슨리</h2>
 * 평범한 그리드면 한 줄의 모든 칸이 그 줄에서 가장 큰 위젯의 바닥에 맞춰 정렬돼(선반처럼)
 * 짧은 위젯 아래에 빈 공간이 생기고 대시보드가 표처럼 뻣뻣해진다. 그래서 그리드를 촘촘한
 * 행으로 깔고, 각 위젯이 자기 내용 높이만큼 <b>행을 몇 칸 쓸지</b>를 재서 정한다.
 *
 * <p>`column-count` 를 쓰지 않는 이유: 순서가 열 단위(위→아래, 그다음 열)로 바뀌어 좌→우
 * 순서가 깨지고 wide 위젯을 못 싣는다.
 *
 * <h2>격자형 — 선반</h2>
 * 위의 「선반처럼」이 바로 격자형이다(사용자 요청 2026-09-11). 한 줄의 위젯이 같은 높이로 맞춰지고
 * 칸이 가지런하다. 높이를 재지 않는다 — 그리드가 스스로 맞춘다. 카드가 칸을 채우도록 늘린다.
 */

/** 그리드 한 행의 크기(px). 작을수록 높이가 촘촘하게 맞는다. */
const ROW_UNIT = 8;
/** 위젯 사이 간격(px). Tailwind `gap-3` 과 같아야 스팬 계산이 맞는다. */
const GAP = 12;

export type WidgetItem = {
  key: string;
  title: string;
  /** 등록부의 크기 — `wide` 는 두 칸을 쓴다(모바일 1열에서는 뜻이 없다). */
  size: WidgetSize;
  node: ReactNode;
};

export function WidgetMasonry({ items, layout = 'masonry' }: { items: WidgetItem[]; layout?: HomeLayout }) {
  const masonry = layout === 'masonry';
  return (
    /*
      grid-cols-1 을 빼면 안 된다 — 모바일에서 열 정의가 없으면 암시적 열이 `auto` 로 잡혀
      가장 넓은 위젯의 최대 콘텐츠 너비까지 늘어나 화면이 통째로 옆으로 밀린다.

      ## 열 수 (2026-09-09 넓힘)

      처음에는 md 까지만 두고 「위젯이 대여섯이라 3열이면 납작해진다」고 적었다. 그 사이 위젯이
      <b>열여섯</b>으로 늘었고, 넓은 화면에서 2열로 세로로 흐르니 아래쪽 위젯은
      스크롤해야만 보인다 — 대시보드가 아니라 목록이 됐다.

        · 모바일(~767)      1열
        · 태블릿·노트북(768~) 2열
        · 넓은 웹(1536~)     3열
        · 아주 넓은 화면(1920~) 4열 — 사무실 모니터에서 열여섯이 한눈에 든다

      사이드바가 lg(1024)에서 자리를 차지하므로({@link AppShell}) 본문 폭이 그만큼 줄어든다.
      그래서 3열을 lg 가 아니라 <b>2xl 부터</b>로 미룬다 — 안 그러면 위젯 하나가 눌려 글자가
      서너 자만 남는다(front 가 md→lg 로 미룬 것과 같은 사정).
    */
    <div
      className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3 min-[1920px]:grid-cols-4"
      style={masonry ? { gridAutoRows: `${ROW_UNIT}px` } : undefined}
    >
      {items.map((item) => {
        const span = item.size === 'wide' ? 'md:col-span-2' : '';
        return masonry ? (
          <MasonryItem key={item.key} className={span}>
            <WidgetBoundary title={item.title}>{item.node}</WidgetBoundary>
          </MasonryItem>
        ) : (
          // 격자형 — 칸은 그리드가 맞추고, 카드는 그 칸을 끝까지 채운다(section 이 card 다).
          <div key={item.key} className={`min-w-0 h-full [&>section]:h-full ${span}`}>
            <WidgetBoundary title={item.title}>{item.node}</WidgetBoundary>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 자유형의 위젯 한 칸. 자기 높이를 재서 행을 몇 칸 쓸지 정한다.
 *
 * <p>행 높이가 고정이라 <b>자리는 스팬이 정한다.</b> 카드가 그 자리보다 크면 잘리지 않고 흘러넘쳐
 * 아래 위젯 위에 겹쳐 그려진다 — 겹침은 그리드가 깨진 것이 아니라 「높이를 덜 잡았다」는 신호다.
 * 그래서 재는 시점을 넉넉히 둔다: 렌더마다 · 크기 변화 · 창 크기 · 웹폰트 적용 완료.
 */
function MasonryItem({ className, children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [span, setSpan] = useState(1);

  useLayoutEffect(() => {
    const wrapper = ref.current;
    if (wrapper === null) return;

    const measure = () => {
      const content = wrapper.firstElementChild;
      const height = content !== null ? content.getBoundingClientRect().height : wrapper.scrollHeight;
      setSpan(Math.max(1, Math.ceil((height + GAP) / (ROW_UNIT + GAP))));
    };
    measure();

    window.addEventListener('resize', measure);
    // 폰트는 레이아웃이 끝난 뒤에 바뀔 수 있다(Pretendard 를 늦게 받는 첫 방문).
    document.fonts?.ready.then(measure).catch(() => {});

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer !== null && wrapper.firstElementChild !== null) observer.observe(wrapper.firstElementChild);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  });

  return (
    // min-w-0 — 그리드 항목의 기본 최소 너비는 `auto` 라, 열이 1fr 이어도 내용이 넓으면 칸을 뚫는다.
    <div ref={ref} className={`min-w-0 ${className ?? ''}`} style={{ gridRowEnd: `span ${span}` }}>
      {children}
    </div>
  );
}
